/**
 * Thin wrapper over the Credential Management API (`navigator.credentials`), used by the login flow
 * so browser password managers can prefill, silently sign in, and reliably offer to save.
 *
 * Chromium-only: `PasswordCredential` is not implemented in Firefox/Safari, where every function here
 * is a no-op and the native `autocomplete` attributes on the form do the work instead.
 */

interface PasswordCredentialData {
    id: string;
    password: string;
    name?: string;
    iconURL?: string;
}

interface StoredCredential {
    username: string;
    password: string;
}

declare global {
    interface Window {
        PasswordCredential?: new (data: PasswordCredentialData) => Credential;
    }
}

function passwordCredentialCtor(): (new (data: PasswordCredentialData) => Credential) | undefined {
    if (typeof window === 'undefined' || !navigator.credentials) return undefined;
    return window.PasswordCredential;
}

/**
 * Ask the browser for a stored login. With `mediation: 'optional'` it may show the account chooser;
 * returns `null` when unsupported, when the user dismisses it, or when nothing is stored.
 */
export async function getStoredCredential(): Promise<StoredCredential | null> {
    if (!passwordCredentialCtor()) return null;
    try {
        const options: CredentialRequestOptions & { password: boolean } = {
            password: true,
            mediation: 'optional',
        };
        const credential = (await navigator.credentials.get(options)) as
            | (Credential & { password?: string | null })
            | null;
        if (credential && typeof credential.password === 'string' && credential.password.length > 0) {
            return { username: credential.id, password: credential.password };
        }
        return null;
    } catch {
        // API blocked by the environment, or the user dismissed the chooser — fall back to manual entry.
        return null;
    }
}

/** Hand the working credentials to the browser so it prompts to save them. */
export async function storeCredential(username: string, password: string): Promise<void> {
    const Ctor = passwordCredentialCtor();
    if (!Ctor) return;
    try {
        await navigator.credentials.store(
            new Ctor({
                id: username,
                password,
                name: username,
                iconURL: `${location.origin}/icons/icon-192.png`,
            }),
        );
    } catch {
        // Non-fatal: saving is a browser affordance, not part of the login result.
        return;
    }
}

/** After logout, stop the browser from silently re-authenticating on the next visit. */
export async function preventSilentAccess(): Promise<void> {
    if (!passwordCredentialCtor()) return;
    try {
        await navigator.credentials.preventSilentAccess();
    } catch {
        // Non-fatal.
        return;
    }
}
